import React, { forwardRef, useEffect, useRef, ReactNode } from 'react';
import clsx from 'clsx';
import { useFocusRing } from 'react-aria/useFocusRing';
import { useHover } from 'react-aria/useHover';
import { usePress } from 'react-aria/usePress';
import { useRadio, AriaRadioProps } from 'react-aria/useRadioGroup';
import { mergeProps } from 'react-aria/mergeProps';
import { useFocusableRef } from '@react-spectrum/utils';
import { FocusableRef } from '@react-types/shared';
import styles from '@danske/sapphire-css/components/radio/radio.module.css';

import { RadioBox } from './RadioBox';
import { useRadioGroupContext } from './context';
import { SapphireStyleProps } from '../../utils';

export type RadioProps = AriaRadioProps &
  SapphireStyleProps & {
    /**
     * @default 'lg'
     */
    size?: 'lg' | 'md';
    /**
     * If set, the radio button displays note text below the main label.
     */
    note?: ReactNode;
  };

export const Radio = forwardRef(function Radio(
  props: RadioProps,
  ref: FocusableRef<HTMLLabelElement>
) {
  const { state, size: groupSize } = useRadioGroupContext();
  const { children, autoFocus, note, ...otherProps } = props;
  const size = groupSize || props.size || 'lg';
  const isDisabled = props.isDisabled || state.isDisabled;
  const { hoverProps, isHovered } = useHover({ isDisabled });

  const inputRef = useRef<HTMLInputElement>(null);
  const domRef = useFocusableRef(ref, inputRef);
  const { inputProps } = useRadio(props, state, inputRef);
  const { focusProps, isFocusVisible } = useFocusRing(props);
  const { pressProps, isPressed } = usePress({});

  useEffect(() => {
    if (
      typeof process !== 'undefined' &&
      process.env.NODE_ENV !== 'production' &&
      props.size &&
      groupSize &&
      props.size !== groupSize
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        `size "${groupSize}" prop set on the RadioGroup overrides the child Radio's size: "${props.size}"`
      );
    }
  }, [props.size, groupSize]);

  return (
    <div className={styles['sapphire-radio-container']}>
      <RadioBox
        {...otherProps}
        {...hoverProps}
        elementType="label"
        ref={domRef}
        label={children}
        size={size}
        isSelected={inputProps.checked}
        isHovered={isHovered}
        isFocusVisible={isFocusVisible}
        isPressed={isPressed}
        isDisabled={state.isDisabled}
      >
        <input
          {...mergeProps(inputProps, focusProps, pressProps)}
          ref={inputRef}
          className={clsx(styles['sapphire-radio__input'])}
        />
      </RadioBox>
      {typeof note === 'string' && (
        <div
          className={clsx(styles['sapphire-radio__note'], {
            [styles['sapphire-radio__note--md']]: size === 'md',
          })}
        >
          {note}
        </div>
      )}
    </div>
  );
});
